/**
 * The map slice a letter may carry.
 *
 * A slice is a rectangle of glyphs at an anchor the sender chose. It is a
 * **claim**, not a record: the sender may have edited every tile of it, and may
 * be describing ground they never walked. Validation checks shape, bounds and
 * glyph legality, and nothing else -- lying is a mechanic (`docs/rules.md`
 * section 5), and truth is judged later by the recipient walking the ground.
 *
 * That is enforced structurally rather than by discipline: `validateSlice` is
 * given the slice and nothing else, so there is no island in scope to compare
 * it against. It is also the reason the parsed slice is rebuilt from exactly
 * five fields -- anonymity R8, nothing derived from the sender's account may
 * travel with an attachment.
 */

import { z } from 'zod'
import { GAME_CONFIG } from '../game-config.ts'
import { SLICE_GLYPHS } from '../tiles.ts'

const { grid, letter } = GAME_CONFIG

/** A slice as it is stored in `attachments_json` and sent over the API. */
export type MapSlice = {
  /** Anchor: the tile the slice's top-left cell describes. */
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
  /** Row-major, `w * h` entries, each one glyph from the map's own set. */
  readonly glyphs: readonly string[]
}

export type SliceError =
  /** Not the shape of a slice at all. */
  | 'malformed'
  /** Wider or taller than a slice may be. */
  | 'too-large'
  /** Some part of the rectangle falls outside the island. */
  | 'off-the-island'
  /** The glyph list does not match `w * h`. */
  | 'wrong-glyph-count'
  /** A character the map cannot draw. */
  | 'illegal-glyph'

export type SliceResult =
  | { readonly ok: true; readonly slice: MapSlice }
  | { readonly ok: false; readonly error: SliceError }

/**
 * The wire shape. Unknown keys are stripped, which is half of anonymity R8:
 * a handler that spread its own state into the object cannot have that reach
 * a recipient.
 */
export const mapSliceSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
  glyphs: z.array(z.string()),
})

const legalGlyphs = new Set(SLICE_GLYPHS)

/** Check a slice's shape, bounds and glyphs. Never its truth. */
export function validateSlice(slice: unknown): SliceResult {
  const parsed = mapSliceSchema.safeParse(slice)
  if (!parsed.success) return { ok: false, error: 'malformed' }
  const { x, y, w, h, glyphs } = parsed.data

  if (w < 1 || h < 1) return { ok: false, error: 'malformed' }
  if (w > letter.slice.maxWidth || h > letter.slice.maxHeight) {
    return { ok: false, error: 'too-large' }
  }
  if (x < 0 || y < 0 || x + w > grid.width || y + h > grid.height) {
    return { ok: false, error: 'off-the-island' }
  }
  if (glyphs.length !== w * h) return { ok: false, error: 'wrong-glyph-count' }
  for (const glyph of glyphs) {
    if (!legalGlyphs.has(glyph)) return { ok: false, error: 'illegal-glyph' }
  }

  return { ok: true, slice: { x, y, w, h, glyphs } }
}

/** The island tile a slice cell describes. Used when charting hearsay. */
export function sliceCellPosition(slice: MapSlice, cell: number): { x: number; y: number } {
  return { x: slice.x + (cell % slice.w), y: slice.y + Math.floor(cell / slice.w) }
}
